import { Link, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { api } from '../../lib/apiClient.js';
import { useMutation } from '../../lib/useApi.js';
import '../auth/personaLogin.css';
import '../auth/auth.redesign.css';
import './publicRequestAccess.css';

function initialDraft(prefilledEmail) {
  return {
    fullName: '',
    email: prefilledEmail || '',
    organization: '',
    justification: '',
    expectedTesterAccessNote: '',
  };
}

export function PublicRequestAccessPage() {
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email') || '';
  const [draft, setDraft] = useState(() => initialDraft(prefilledEmail));
  const [submitted, setSubmitted] = useState(null);
  const mutation = useMutation();

  async function handleSubmit(event) {
    event.preventDefault();
    const response = await mutation.run(() => api.post('/public-access-requests', draft));
    setSubmitted(response?.data || response);
  }

  const backToLogin = `/login${prefilledEmail ? `?email=${encodeURIComponent(prefilledEmail)}` : ''}`;

  return (
    <div
      className="persona-login-shell public-request-access-shell"
      data-testid="public-request-access-page"
    >
      <div className="persona-login-hero">
        <div className="login-brand">
          {/* PRIME is the primary brand (PRIME-BRAND-UX-001); the DHA emblem is
              a secondary affiliation below the headline. */}
          <div className="login-brand-copy">
            <div className="login-kicker">Private UAT entry</div>
            <h1>DHA PRIME</h1>
            <div className="login-acronym">
              Program Resource and Intelligence Management Engine
            </div>
          </div>
          <div className="login-affiliation">
            <span className="login-brand-mark login-brand-seal">
              <img src="/brand/dha-seal.svg" alt="Defense Health Agency seal" />
            </span>
            <span className="login-affiliation-text">Defense Health Agency</span>
          </div>
        </div>
        <p className="login-blurb">
          We don&apos;t recognize this email yet. Submit a PMHub testing access request and a root
          admin will review it, assign the correct sandbox, and activate routing for this email.
        </p>
        <ul className="login-bullets">
          <li>The shared PMHub host stays email-first and does not require CAC for this step</li>
          <li>Your request is matched to the email you expect PMHub to recognize later</li>
          <li>The dedicated admin CAC host is used only by the root admin operator</li>
        </ul>
      </div>

      <div className="persona-login-card">
        {submitted ? (
          <section className="auth-entry-panel public-request-access-panel">
            <div className="auth-entry-copy">
              <span className="eyebrow">Request received</span>
              <h2>We&apos;ll route this email after approval</h2>
              <p className="muted">
                <strong>{submitted.email}</strong> was saved for PMHub access review. A root admin
                must approve the request, assign a sandbox, and create the tester account before
                this email will route to sign-in automatically.
              </p>
            </div>

            <div className="banner blue">
              Next steps: watch for follow-up from the PMHub coordinator, then return to the public
              PMHub front door and enter the same email again after approval.
            </div>

            <div className="public-request-access-summary">
              <div>
                <span className="eyebrow">Saved email</span>
                <strong>{submitted.email}</strong>
              </div>
              <div>
                <span className="eyebrow">Organization</span>
                <strong>{submitted.organization || 'Not provided'}</strong>
              </div>
            </div>

            <div className="auth-form-stack">
              <Link className="btn primary" to={backToLogin}>
                Return to PMHub front door
              </Link>
            </div>
          </section>
        ) : (
          <section className="auth-entry-panel public-request-access-panel">
            <div className="auth-entry-copy">
              <span className="eyebrow">Request access</span>
              <h2>We don&apos;t recognize this email yet</h2>
              <p className="muted">
                Tell us who should be routed, where you work, and why you expected PMHub tester
                access. No CAC details are required on this page.
              </p>
            </div>

            <form className="auth-form-stack" onSubmit={handleSubmit}>
              <label className="form-field">
                <span className="form-label">Full name</span>
                <input
                  className="form-input"
                  type="text"
                  autoFocus
                  value={draft.fullName}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, fullName: event.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="form-label">Email address</span>
                <input
                  className="form-input"
                  type="email"
                  value={draft.email}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, email: event.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="form-label">Organization</span>
                <input
                  className="form-input"
                  type="text"
                  value={draft.organization}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, organization: event.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="form-label">Why do you need PMHub testing access?</span>
                <textarea
                  className="form-input public-request-access-textarea"
                  value={draft.justification}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, justification: event.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="form-label">Optional note</span>
                <textarea
                  className="form-input public-request-access-textarea"
                  value={draft.expectedTesterAccessNote}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      expectedTesterAccessNote: event.target.value,
                    }))
                  }
                  placeholder="For example: I expected this email to already have tester access."
                />
              </label>
              <button className="btn primary" type="submit" disabled={mutation.running}>
                {mutation.running ? 'Submitting request…' : 'Submit request'}
              </button>
            </form>

            <div className="auth-support-note">
              <strong>Already approved?</strong>
              <span>
                Return to the PMHub front door and enter the same email again after the root admin
                assigns your sandbox.
              </span>
            </div>

            {mutation.error ? <div className="banner red">{mutation.error.message}</div> : null}
          </section>
        )}
      </div>
    </div>
  );
}
